import type { PanelId } from '@/types/panels';

/**
 * Centralised panel coexistence rules — **default-deny** model.
 *
 *   Two panels can be open at the same time ONLY when at least one of
 *   the following holds:
 *     - they share at least one entry in COMPATIBILITY_GROUPS, or
 *     - one of them is listed in UNIVERSAL_PANELS.
 *
 *   Otherwise opening one closes the other.
 *
 * This file is the single source of truth: panel configs do NOT declare
 * exclusions locally. The registry takes the groups + universal set
 * and, knowing which panels are currently registered, materialises each
 * panel's `config.excludes` list (= "every other registered panel that
 * is NOT compatible with me"). resolveOpen and friends keep reading
 * `config.excludes` exactly as before — only the way the map is built
 * has changed.
 *
 * Adding / removing a coexistence rule:
 *   - To make a new panel coexist with an existing workflow, add its id
 *     to the relevant group.
 *   - A panel can appear in multiple groups (compatibility is the union).
 *   - A panel that appears in no group and isn't universal coexists with
 *     nothing except the universal panels. That's deliberate — opening
 *     it gives that panel the whole stage (plus AI Chat).
 */

/**
 * Compatibility groups. Order doesn't matter; group membership is what
 * counts. A panel that appears in multiple groups can sit beside every
 * panel from any of those groups (compatibility is unioned, not
 * intersected).
 */
// The live events drawer is absent from every group on purpose: it is
// UNIVERSAL_PANELS below, so it reads over any workflow rather than needing a
// seat in each one.
export const COMPATIBILITY_GROUPS: PanelId[][] = [
    // 1. The cameras workflow: pick a camera, watch it, configure it.
    //    Deliberately narrow — the tree is a navigator for the camera surfaces,
    //    not a sidebar that tags along beside every list in the app, so opening
    //    Events / Objects / Motion / Video search (or any settings list) closes
    //    it and vice versa. Layouts is a TAB of cameras_list.
    ['cameras_list', 'camera_view', 'camera_settings'],

    // 2. Rules: combined list/editor + its activation-estimate drawer.
    ['rules', 'rule_estimate'],

    // 2a. Rule flows (experimental): the flow scene + the settings of its selected card.
    ['rule_flow', 'rule_flow_details'],

    // 3. Object browsing: the list + the preview it seeks. Known objects is a
    //    TAB of objects_list, not a panel of its own.
    ['objects_list', 'camera_view'],

    // 3a. Enrolled objects: the Known objects tab of objects_list as a panel of its own, + the
    //     preview its appearances seek.
    ['enrolled_objects', 'camera_view'],

    // 4. Event browsing: the list + the preview it seeks.
    ['events_list', 'camera_view'],

    // 5. Servers: the panel shows the agent list of the analytics server, and the editor of
    //    the selected agent opens beside it.
    ['servers', 'agent'],

    // 6. Users: list + selected-user detail.
    ['users', 'user_details'],

    // 7. Video search: scene-embedding vector search list + live preview.
    //    Shares the "primary list" slot with objects_list / events_list (it's in
    //    no group with them, so opening it closes them).
    ['video_search', 'camera_view'],

    // 7a. AI Search (experimental): objects, events and video search in one list, + the
    //     preview it seeks. It shares the "primary list" slot with the lists it combines.
    ['ai_search', 'camera_view'],

    // 8. Motion: per-camera motion feed + live preview. Like the other
    //    browse lists it shares the "primary list" slot with
    //    objects_list / events_list / video_search (in no group with them, so
    //    opening Motion closes whichever was up), while camera_view stays
    //    available to seek to a motion moment.
    ['motion_list', 'camera_view'],

    // 9. One case: its details + whichever surface the evidence it holds
    //    wants. A video hands the whole case to the one video surface, exactly
    //    as the Layouts tab of the cameras list hands it a layout; anything else
    //    opens the viewer built for that kind of file. Those viewers share no
    //    group with camera_view, so opening a document puts the video away and
    //    closing it brings back whatever the row had before. The case list, a
    //    tab of Bookmarks, is in no group with them: opening a case gives the
    //    stage to the case.
    ['case_details', 'camera_view'],
    ['case_details', 'case_image'],
    ['case_details', 'case_video'],
    ['case_details', 'case_document'],

    // 10. Bookmarks: the list + live preview, like the other browse lists.
    //     It shares the "primary list" slot with them (no shared group), and
    //     camera_view stays available to seek to a bookmarked moment.
    ['bookmarks', 'camera_view'],

    // 11. Audit log: the trail + the camera at a record's moment, beside it. Any
    //     other panel, the object a record is about included, takes the stage
    //     from the log. The log takes it from the camera too: see OPENING_CLOSES.
    ['audit_log', 'camera_view'],
];

/**
 * Panels that coexist with every other panel — they sit on top of any
 * workflow without disturbing it. Keep this set very small; everything
 * here is effectively unconstrained.
 */
export const UNIVERSAL_PANELS: ReadonlySet<PanelId> = new Set<PanelId>([
    'ai_chat',
    // The live events drawer: a small ephemeral feed off the WebRTC bus that
    // reads over whatever you are doing rather than replacing it. It was listed
    // in seven groups instead, which made it exclusive with everything nobody had
    // thought to add it to (rules, servers, users, the cameras tree). Being
    // universal is what its own config always claimed (no excludes). It is
    // dual-mode to match so it cannot evict the settings workflow it claims to
    // overlay.
    'notifications',
]);

/**
 * One-way evictions: opening the key closes these panels, though they may
 * still open beside it. A detail another list left on stage would otherwise
 * sit beside a list that shows nothing selected.
 */
export const OPENING_CLOSES: Partial<Record<PanelId, PanelId[]>> = {
    // The log opens alone: the camera shows beside it only for a moment a record picks.
    audit_log: ['camera_view'],
};

/**
 * Materialise per-panel `excludes` lists from the compatibility model.
 *
 *   excludes(P) = { Q ∈ registeredIds : Q ≠ P AND NOT areCompatible(P, Q) }
 *
 * Where areCompatible(P, Q) is true iff P or Q is universal, or P and Q
 * appear together in at least one COMPATIBILITY_GROUPS entry. P's
 * OPENING_CLOSES entries are added to excludes(P) alone.
 *
 * Generic over the id type so the algorithm can be tested on its own
 * letters; the shipped call in registry.ts supplies `PanelId` throughout.
 */
export function buildExclusionMap<Id extends string>(
    groups: readonly (readonly Id[])[],
    registeredIds: ReadonlySet<Id>,
    universalIds: ReadonlySet<Id> = new Set<Id>(),
    openingCloses: Partial<Record<Id, readonly Id[]>> = {},
): Partial<Record<Id, Id[]>> {
    const groupSets = groups.map((group) => new Set<Id>(group));
    const areCompatible = (a: Id, b: Id): boolean => (
        universalIds.has(a) || universalIds.has(b) ||
        groupSets.some((group) => group.has(a) && group.has(b))
    );

    const outValue: Partial<Record<Id, Id[]>> = {};
    for (const id of registeredIds) {
        const closes = new Set<Id>(openingCloses[id] ?? []);
        outValue[id] = [...registeredIds].filter((other) => (
            other !== id && (closes.has(other) || !areCompatible(id, other))
        ));
    }
    return outValue;
}

/**
 * The largest sets of panels that could share the stage, and every set that
 * reaches that size.
 *
 * Group length is the wrong measure: compatibility is pairwise and unioned, so
 * groups [a,b], [b,c] and [a,c] seat three with no group longer than two. This
 * reads the materialised map and asks who excludes whom — a pair is seatable
 * only when NEITHER excludes the other, which is also what accounts for the
 * one-way evictions.
 *
 * `ids` is the population to search: every panel for the mode-blind number, one
 * mode's panels for the number that can actually happen. Universal and
 * off-stage panels count — an off-stage panel is still open, still resolved the
 * same way, and still part of what a person is looking at.
 */
export function getMaxCoexisting<Id extends string>(
    excludes: Partial<Record<Id, readonly Id[]>>, ids: readonly Id[],
): Id[][] {
    const canSeat = (panel: Id, other: Id): boolean => (
        !(excludes[panel] ?? []).includes(other) && !(excludes[other] ?? []).includes(panel)
    );

    const bestItems: Id[][] = [];
    const record = (clique: Id[]): void => {
        const size = bestItems[0]?.length ?? 0;
        if (clique.length > size) {
            bestItems.length = 0;
        }
        if (clique.length >= size) {
            bestItems.push([...clique]);
        }
    };
    // Reaches every set exactly once: each step takes a candidate from after the
    // last one taken, keeping only those seatable beside it.
    const grow = (clique: Id[], candidates: Id[]): void => {
        record(clique);
        candidates.forEach((id, at) => grow(
            [...clique, id], candidates.slice(at + 1).filter((other) => canSeat(id, other))));
    };
    grow([], [...ids]);
    return bestItems;
}
